import React from 'react'
import Aside from './Aside'
import Card from './Card'
import PromoRow from './PromoRow'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import { IEcommerceQuestionSchema, EcommerceResponse } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { countDrawnCards, drawnCards } from '@aytm/app/swidget/base/questions/Ecommerce/utils/hiddenClass.ts'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { CardsSkeleton } from './Skeleton'
import style from './Main.module.scss'

interface IMainProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  deliveryDates: number[]
  findability: boolean
  onAddToCart: (qnt: 1 | -1, index: number) => void
  onItemSelect: (index: number) => void
  registerCardRef: (id: string, element: HTMLDivElement | null) => void
  resetCenterElementRef: () => void
}

const Main = ({
  schema,
  response,
  deliveryDates,
  findability,
  onAddToCart,
  onItemSelect,
  registerCardRef,
  resetCenterElementRef
}: IMainProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const sponsoredSize = schema.answers.filter(answer => answer.featured).length
  // the header counts the cards the grid renders: children sit inside their parent's card, banner and
  // featured products have rows of their own, none-of-the-above is not a product, and a preview-hidden card is
  // not drawn
  const gridAnswers = schema.answers.filter(
    answer => !answer.parent_product && !answer.na && !answer.featured && !answer.banner
  )
  const resultsCount = countDrawnCards(gridAnswers)
  // The sponsored row and the grid each wait for their own pictures behind outlines of their cards, side by
  // side with the promo row; the toolbar, the sidebar and the heading never wait. The cards draw the
  // transparent variant, so that is the one waited for.
  const sponsored = schema.answers.filter(answer => !answer.na && answer.featured)
  const sponsoredImages = sponsored.map(answer => answer.image && getMediumAssetPath(answer.image, 'transparent'))
  const gridImages = drawnCards(gridAnswers).map(
    answer => answer.image && getMediumAssetPath(answer.image, 'transparent')
  )
  return (
    <>
      <div className={`${style.main} ${viewport.mobile ? style.mainMobile : ''}`} onClick={resetCenterElementRef}>
        <PromoRow
          schema={schema}
          response={response}
          findability={findability}
          onAddToCart={onAddToCart}
          onItemSelect={onItemSelect}
          deliveryDates={deliveryDates}
        />
      </div>
      {sponsoredSize ? (
        <div className={`${style.main} ${viewport.mobile ? style.mainMobile : ''}`} onClick={resetCenterElementRef}>
          <div className={` ${style.carouselContainer}`}>
            <div className={` ${style.carousel}`}>
              <AssetsGate
                urls={sponsoredImages}
                loader={<CardsSkeleton count={Math.min(sponsored.length, 5)} kind="small" />}
              >
                {sponsored.map((answer, index) => (
                  <Card
                    key={index}
                    data-testid="SW-EC-CarouselItem"
                    answer={answer}
                    small={true}
                    bannerPlacement="featured"
                    response={response}
                    questionSchema={schema}
                    index={answer.index}
                    findability={findability}
                    onAddToCart={onAddToCart}
                    disabled={'focus' == schema.mode_type && !answer.focused}
                    onItemSelect={onItemSelect}
                    deliveryDate={deliveryDates[answer.index]}
                  />
                ))}
              </AssetsGate>
            </div>
          </div>
        </div>
      ) : (
        ''
      )}
      <div className={`${style.options} ${viewport.mobile ? style.optionsMobile : ''}`}>
        <div className={style.buttons}>
          {viewport.mobile && (
            <EmptyLink color="#0053e2">
              <div className={style.item}>
                <i className="wm-Filter" />
                <span className={style.text}>{t('questions', 'ec', 'walmart', 'filters')}</span>
                <i className="wm-ChevronDown" />
              </div>
            </EmptyLink>
          )}
          <EmptyLink color="#0053e2">
            <div className={style.item}>
              <i className="wm-Store" />
              <span className={style.text}>{t('questions', 'ec', 'walmart', 'inStore')}</span>
            </div>
          </EmptyLink>
          <EmptyLink color="#0053e2">
            <div className={style.item}>
              <i className="wm-MoneyCircle" />
              <span className={style.text}>{t('questions', 'ec', 'walmart', 'price')}</span>
              <i className="wm-ChevronDown" />
            </div>
          </EmptyLink>
          <EmptyLink color="#0053e2">
            <div className={style.item}>
              <span className={style.text}>{t('questions', 'ec', 'walmart', 'brand')}</span>
              <i className="wm-ChevronDown" />
            </div>
          </EmptyLink>
          <EmptyLink color="#0053e2">
            <div className={style.item}>
              <i className="wm-Clock" />
              <span className={style.text}>{t('questions', 'ec', 'walmart', 'fullfillment')}</span>
              <i className="wm-ChevronDown" />
            </div>
          </EmptyLink>
        </div>
        {!viewport.mobile && (
          <EmptyLink color="#0053e2">
            <div className={style.sort}>
              <strong>{t('questions', 'ec', 'walmart', 'sortBy')}</strong>
              <span>{t('questions', 'ec', 'walmart', 'bestMatch')}</span>
              <i className="wm-ChevronDown" />
            </div>
          </EmptyLink>
        )}
      </div>
      <div className={`${style.main} ${viewport.mobile ? style.mainMobile : ''}`}>
        <Aside></Aside>
        <div className={style.products}>
          <div className={style.head}>
            {schema.search_condition && (
              <div className={style.results}>
                <span
                  className={style.alignedText}
                  dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'walmart', 'results') }}
                />
                <span className={style.alignedText}>&#34;</span>
                <span className={style.alignedText} dangerouslySetInnerHTML={{ __html: schema.search_condition }} />
                <span className={style.alignedText}>&#34;</span>
                <span className={style.count}>{` (${resultsCount})`}</span>
              </div>
            )}
            <div className={style.note}>
              <span className={style.noteText}>{t('questions', 'ec', 'walmart', 'external')}</span>
              <EmptyLink color="#0053e2">
                <i className="pi pi-info-circle" />
              </EmptyLink>
            </div>
          </div>
          <div className={style.list}>
            <AssetsGate
              urls={gridImages}
              loader={<CardsSkeleton count={gridImages.length} kind="grid" perRow={schema.products_per_row ?? null} />}
            >
              {schema.answers.map((answer, index) =>
                !answer.parent_product && !answer.na && !answer.featured && !answer.banner ? (
                  <Card
                    key={answer.id}
                    ref={element => registerCardRef(answer.id, element)}
                    data-testid="SW-EC-Item"
                    answer={answer}
                    response={response}
                    questionSchema={schema}
                    index={index}
                    findability={findability}
                    disabled={'focus' == schema.mode_type && !answer.focused}
                    applyCustomWidth={true}
                    onAddToCart={onAddToCart}
                    onItemSelect={onItemSelect}
                    deliveryDate={deliveryDates[index]}
                  />
                ) : null
              )}
            </AssetsGate>
          </div>
        </div>
      </div>
    </>
  )
}

export default Main
